import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FiMapPin, FiCalendar, FiDollarSign, FiUsers, FiArrowRight, FiSearch } from 'react-icons/fi';
import { formatarData } from '../../utils/dateUtils';
import { getImageUrl } from '../../utils/imageUtils';

const ListaViagens = () => {
  const [viagens, setViagens] = useState([]);
  const [viagensFiltradas, setViagensFiltradas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');

  useEffect(() => {
    fetchViagens();
  }, []);

  useEffect(() => {
    if (busca.trim() === '') {
      setViagensFiltradas(viagens);
    } else {
      const filtradas = viagens.filter(viagem =>
        viagem.titulo.toLowerCase().includes(busca.toLowerCase()) ||
        viagem.destino.toLowerCase().includes(busca.toLowerCase())
      );
      setViagensFiltradas(filtradas);
    }
  }, [busca, viagens]);

  const fetchViagens = async () => {
    try {
      const response = await axios.get('/api/viagens/public');
      setViagens(response.data);
      setViagensFiltradas(response.data);
    } catch (error) {
      console.error('Erro ao carregar viagens:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-sky-600 to-sky-500 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold mb-4">Nossos Pacotes de Viagem</h1>
          <p className="text-xl text-sky-100">Encontre o destino perfeito para sua próxima aventura</p>
        </div>
      </section>

      {/* Busca */}
      <section className="py-8 bg-sky-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por destino ou nome da viagem..."
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
            />
          </div>
        </div>
      </section>

      {/* Lista de Viagens */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
            </div>
          ) : viagensFiltradas.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">
                {busca ? 'Nenhuma viagem encontrada com sua busca.' : 'Nenhuma viagem disponível no momento.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {viagensFiltradas.map((viagem) => {
                const imagemPrincipal = viagem.imagens && Array.isArray(viagem.imagens) && viagem.imagens.length > 0
                  ? getImageUrl(viagem.imagens[0])
                  : null;

                return (
                  <div
                    key={viagem.id}
                    className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow border border-gray-100"
                  >
                    {imagemPrincipal && (
                      <div className="h-48 bg-gray-200 overflow-hidden">
                        <img
                          src={imagemPrincipal}
                          alt={viagem.titulo}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{viagem.titulo}</h3>
                      <div className="space-y-2 mb-4 text-gray-600">
                        <div className="flex items-center">
                          <FiMapPin className="mr-2 text-sky-600" size={18} />
                          {viagem.destino}
                        </div>
                        <div className="flex items-center">
                          <FiCalendar className="mr-2 text-sky-600" size={18} />
                          {formatarData(viagem.data_ida)}
                        </div>
                        <div className="flex items-center">
                          <FiUsers className="mr-2 text-sky-600" size={18} />
                          {viagem.lotado ? (
                            <span className="text-red-600 font-semibold">Lotado - Não há mais poltronas</span>
                          ) : (
                            <span>{viagem.vagas_disponiveis || viagem.vagas || 0} poltronas disponíveis</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                        <div>
                          <p className="text-sm text-gray-500">A partir de</p>
                          <p className="text-3xl font-bold text-sky-600">
                            R$ {parseFloat(viagem.valor || 0).toFixed(2)}
                          </p>
                        </div>
                        {viagem.lotado ? (
                          <div className="flex items-center px-6 py-3 bg-gray-400 text-white rounded-lg cursor-not-allowed font-medium">
                            Lotado
                          </div>
                        ) : (
                          <Link
                            to={`/viagem/${viagem.id}`}
                            className="flex items-center px-6 py-3 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors font-medium"
                          >
                            Ver Detalhes
                            <FiArrowRight className="ml-2" size={18} />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ListaViagens;
